import { isCommandOrCtrlKeyActive } from '@jusbrasil-web/shared-utils-dom'
// eslint-disable-next-line no-restricted-imports
import isEqual from 'lodash/isEqual'
import { v4 as uuid } from 'uuid'

const LEFT_BUTTON_DOWN = 0
const RIGHT_BUTTON_DOWN = 2
const EXECUTION_INTERVAL = 500

class ListenerHelper {
  public listener: EventListenerOrEventListenerObject
  public element: HTMLElement
  public intervalId: NodeJS.Timeout | undefined

  constructor(listener: EventListenerOrEventListenerObject, element: HTMLElement) {
    this.listener = listener
    this.element = element
  }

  private addListeners(): void {
    window.addEventListener('copy', this.listener, true)
    window.addEventListener('dragstart', this.listener, true)
  }

  private removeListeners(): void {
    window.removeEventListener('copy', this.listener, true)
    window.removeEventListener('dragstart', this.listener, true)
  }

  public enable(): void {
    clearInterval(this.intervalId)
    this.addListeners()
    //we need to re add the listeners in order to avoid cheat in copy paywall
    this.intervalId = setInterval(() => {
      this.removeListeners()
      this.addListeners()
    }, EXECUTION_INTERVAL)
  }

  public disable(): void {
    clearInterval(this.intervalId)
    this.removeListeners()
  }
}

export class CopyListenerHelper extends ListenerHelper {
  private readonly randomId: string

  private lastAction: string | null
  private lastMouseButtons: unknown[]
  private copyActionRightButton: unknown[]
  private isCopyAction: boolean

  /**
   * @description Helper to enable or disable copy events inside certain element.
   * @param listener Function to use as callback of copy event.
   * @param element Element which the listener should be attached.
   */
  constructor(listener: EventListenerOrEventListenerObject, element: HTMLElement) {
    super(listener, element)

    this.randomId = `c${uuid().replace(/\W/g, '')}`

    this.lastAction = null
    this.lastMouseButtons = []
    this.copyActionRightButton = [RIGHT_BUTTON_DOWN, LEFT_BUTTON_DOWN]
    this.isCopyAction = false
  }

  private onBlur = () => {
    const elements: Element[] = [this.element]

    this.element.querySelectorAll('*').forEach((node) => {
      elements.push(node)
    })

    const style = document.createElement('style')
    style.className = this.randomId

    const css = `.${this.randomId} { user-select: none; }`

    style.appendChild(document.createTextNode(css))
    document.head.appendChild(style)

    elements.forEach((elem) => {
      elem.classList.add(this.randomId)
    })
  }

  private onFocus = () => {
    Array.from(document.querySelectorAll(`head style.${this.randomId}`)).forEach((elem) => {
      if (elem.parentNode) {
        elem.parentNode.removeChild(elem)
      }
    })

    Array.from(document.querySelectorAll(`.${this.randomId}`)).forEach((elem) => {
      elem.classList.remove(this.randomId)
      if (elem.classList.length === 0) {
        elem.removeAttribute('class')
      }
    })
  }

  private onMouseDown = (event: MouseEvent) => {
    this.lastAction = 'mouseDown'

    if (this.lastMouseButtons.length > 1) {
      this.lastMouseButtons.shift()
    }

    this.lastMouseButtons.push(event.button)
    this.isCopyAction = this.verifyMouseCopyAction()
  }

  private onKeyDown = (event: KeyboardEvent) => {
    this.lastAction = 'keyDown'
    this.isCopyAction = this.verifyKeyboardCopyAction(event)
  }

  public override enable(): void {
    super.enable()
    window.addEventListener('blur', this.onBlur)
    window.addEventListener('focus', this.onFocus)
    window.addEventListener('mousedown', this.onMouseDown)
    window.addEventListener('keydown', this.onKeyDown)
  }

  public override disable(): void {
    super.disable()
    window.removeEventListener('blur', this.onBlur)
    window.removeEventListener('focus', this.onFocus)
    window.removeEventListener('mousedown', this.onMouseDown)
    window.removeEventListener('keydown', this.onKeyDown)
  }

  public getContextCopy(): string {
    if (this.lastAction === 'keyDown' && this.isCopyAction) {
      return 'ctrl+c'
    }

    if (this.lastAction === 'mouseDown' && this.isCopyAction) {
      return 'context menu'
    }

    return 'right button'
  }

  private verifyMouseCopyAction(): boolean {
    return isEqual(this.copyActionRightButton, this.lastMouseButtons)
  }

  private verifyKeyboardCopyAction(event: KeyboardEvent): boolean {
    const keys = ['c']
    return keys.includes(event.key) && isCommandOrCtrlKeyActive(event)
  }
}
